"use client";

import {
	AlertCircle,
	Ban,
	BarChart3,
	CheckCircle2,
	ChevronRight,
	Clock,
	Database,
	ExternalLink,
	GitBranch,
	GitCommit,
	Globe,
	History,
	Key,
	Loader2,
	Lock,
	MailWarning,
	Play,
	RefreshCw,
	ShieldCheck,
	Sparkles,
	Table as TableIcon,
	UserPlus,
	Users,
	XCircle,
} from "lucide-react";
import { useState } from "react";

import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	formatDuration,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Tag,
	Textarea,
	Time,
} from "@meetploy/storybook";

export function AutoDeploysDemo() {
	const deployments = [
		{
			id: "dep_1a2b3c",
			branch: "main",
			commit: "feat: add user authentication",
			commitHash: "a1b2c3d",
			status: "success",
			url: "https://myapp.ploy.app",
			createdAt: "2 minutes ago",
		},
		{
			id: "dep_4d5e6f",
			branch: "feature/dark-mode",
			commit: "style: implement dark mode toggle",
			commitHash: "e4f5g6h",
			status: "building",
			url: "https://feature-dark-mode-myapp.ploy.app",
			createdAt: "5 minutes ago",
		},
		{
			id: "dep_7g8h9i",
			branch: "fix/login-bug",
			commit: "fix: resolve login redirect issue",
			commitHash: "i7j8k9l",
			status: "success",
			url: "https://fix-login-bug-myapp.ploy.app",
			createdAt: "1 hour ago",
		},
		{
			id: "dep_0j1k2l",
			branch: "main",
			commit: "chore: update dependencies",
			commitHash: "m0n1o2p",
			status: "success",
			url: "https://myapp.ploy.app",
			createdAt: "3 hours ago",
		},
	];

	return (
		<Card>
			<CardHeader>
				<CardTitle>Recent Deployments</CardTitle>
				<CardDescription>
					Your latest deployments across all branches
				</CardDescription>
			</CardHeader>
			<CardContent>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Branch</TableHead>
							<TableHead>Commit</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>Preview</TableHead>
							<TableHead>Created</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{deployments.map((deployment) => (
							<TableRow key={deployment.id}>
								<TableCell>
									<div className="flex items-center gap-2">
										<GitBranch className="h-4 w-4 text-muted-foreground" />
										<span className="font-medium">{deployment.branch}</span>
									</div>
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<GitCommit className="h-4 w-4 text-muted-foreground" />
										<span className="text-sm text-muted-foreground">
											{deployment.commitHash}
										</span>
										<span className="text-sm truncate max-w-[200px]">
											{deployment.commit}
										</span>
									</div>
								</TableCell>
								<TableCell>
									<DeploymentStatusBadge status={deployment.status} />
								</TableCell>
								<TableCell>
									<a
										href={deployment.url}
										className="flex items-center gap-1 text-sm text-primary hover:underline"
									>
										<ExternalLink className="h-3 w-3" />
										Visit
									</a>
								</TableCell>
								<TableCell className="text-sm text-muted-foreground">
									{deployment.createdAt}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	);
}

function DeploymentStatusBadge({ status }: { status: string }) {
	switch (status) {
		case "success":
			return (
				<Tag variant="success" icon={<CheckCircle2 className="h-3 w-3" />}>
					Success
				</Tag>
			);
		case "building":
			return (
				<Tag variant="blue" icon={<Loader2 className="h-3 w-3 animate-spin" />}>
					Building
				</Tag>
			);
		case "failed":
			return (
				<Tag variant="error" icon={<XCircle className="h-3 w-3" />}>
					Failed
				</Tag>
			);
		default:
			return <Tag>{status}</Tag>;
	}
}

export function WorkersDemo() {
	const workerCode = `// api/hello.ts
export default {
  async fetch(request, env) {
    const url = new URL(request.url);

    if (url.pathname === "/api/users") {
      const users = await env.DB.prepare(
        "SELECT id, name, email FROM users LIMIT 10"
      ).all();

      return Response.json({ users: users.results });
    }

    return Response.json({
      message: "Hello from the edge!",
      timestamp: new Date().toISOString()
    });
  }
}`;

	return (
		<div className="space-y-6">
			<div className="grid gap-4 md:grid-cols-3">
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium">
							Requests Today
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">1,284,392</div>
						<p className="text-xs text-muted-foreground">+12% from yesterday</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium">Avg Latency</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">12ms</div>
						<p className="text-xs text-muted-foreground">P95: 45ms</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium">Error Rate</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">0.02%</div>
						<p className="text-xs text-muted-foreground">3 errors today</p>
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Worker Code</CardTitle>
					<CardDescription>
						Example API worker running at the edge
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="rounded-lg border border-border bg-zinc-950 overflow-hidden">
						<div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-800">
							<div className="flex gap-1.5">
								<div className="w-3 h-3 rounded-full bg-red-500" />
								<div className="w-3 h-3 rounded-full bg-yellow-500" />
								<div className="w-3 h-3 rounded-full bg-green-500" />
							</div>
							<span className="text-sm text-zinc-400">api/hello.ts</span>
						</div>
						<pre className="p-4 overflow-x-auto">
							<code className="text-sm text-zinc-300 font-mono whitespace-pre">
								{workerCode}
							</code>
						</pre>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

export function StaticSitesDemo() {
	const assets = [
		{ name: "index.html", size: "4.2 KB", cached: true, hits: "45.2K" },
		{ name: "main.js", size: "124 KB", cached: true, hits: "45.1K" },
		{ name: "styles.css", size: "32 KB", cached: true, hits: "45.1K" },
		{ name: "logo.svg", size: "2.1 KB", cached: true, hits: "44.8K" },
		{ name: "fonts/inter.woff2", size: "98 KB", cached: true, hits: "44.5K" },
	];

	const regions = [
		{ name: "North America", latency: "12ms", status: "healthy" },
		{ name: "Europe", latency: "18ms", status: "healthy" },
		{ name: "Asia Pacific", latency: "45ms", status: "healthy" },
		{ name: "South America", latency: "65ms", status: "healthy" },
	];

	return (
		<div className="grid gap-6 md:grid-cols-2">
			<Card>
				<CardHeader>
					<CardTitle>Cached Assets</CardTitle>
					<CardDescription>
						All assets are cached at edge locations
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>File</TableHead>
								<TableHead>Size</TableHead>
								<TableHead>Cache Hits</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{assets.map((asset) => (
								<TableRow key={asset.name}>
									<TableCell className="font-mono text-sm">
										{asset.name}
									</TableCell>
									<TableCell className="text-muted-foreground">
										{asset.size}
									</TableCell>
									<TableCell>
										<Tag>{asset.hits}</Tag>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Edge Locations</CardTitle>
					<CardDescription>Global distribution status</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						{regions.map((region) => (
							<div
								key={region.name}
								className="flex items-center justify-between"
							>
								<div className="flex items-center gap-3">
									<Globe className="h-4 w-4 text-muted-foreground" />
									<span>{region.name}</span>
								</div>
								<div className="flex items-center gap-3">
									<span className="text-sm text-muted-foreground">
										{region.latency}
									</span>
									<Tag
										variant="success"
										icon={<CheckCircle2 className="h-3 w-3" />}
									>
										Healthy
									</Tag>
								</div>
							</div>
						))}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

export function DatabaseDemo() {
	const [selectedTable, setSelectedTable] = useState("users");
	const [query, setQuery] = useState("SELECT * FROM users LIMIT 10;");

	const tables = [
		{ name: "users", rows: 1247 },
		{ name: "orders", rows: 8934 },
		{ name: "products", rows: 156 },
		{ name: "sessions", rows: 3421 },
	];

	const userData = [
		{ id: 1, name: "Alice Johnson", email: "alice@example.com", active: true },
		{ id: 2, name: "Bob Smith", email: "bob@example.com", active: true },
		{ id: 3, name: "Carol White", email: "carol@example.com", active: false },
		{ id: 4, name: "David Brown", email: "david@example.com", active: true },
		{ id: 5, name: "Eve Davis", email: "eve@example.com", active: true },
	];

	const topQueries = [
		{
			query: "SELECT * FROM users WHERE active = ?",
			calls: 12453,
			avgTime: "2.3ms",
			rowsRead: 45231,
		},
		{
			query: "SELECT * FROM orders WHERE user_id = ?",
			calls: 8721,
			avgTime: "4.1ms",
			rowsRead: 89234,
		},
		{
			query: "INSERT INTO sessions (user_id, token) VALUES (?, ?)",
			calls: 5432,
			avgTime: "1.2ms",
			rowsRead: 0,
		},
		{
			query: "SELECT p.*, c.name FROM products p JOIN categories c...",
			calls: 3211,
			avgTime: "8.7ms",
			rowsRead: 156432,
		},
		{
			query: "UPDATE users SET last_login = ? WHERE id = ?",
			calls: 2145,
			avgTime: "1.8ms",
			rowsRead: 2145,
		},
	];

	const backups = [
		{
			id: "snap_a1b2c3",
			type: "Scheduled",
			size: "24.5 MB",
			createdAt: "2024-01-15 10:00:00",
			status: "completed",
		},
		{
			id: "snap_d4e5f6",
			type: "Scheduled",
			size: "24.3 MB",
			createdAt: "2024-01-15 06:00:00",
			status: "completed",
		},
		{
			id: "snap_g7h8i9",
			type: "Manual",
			size: "24.1 MB",
			createdAt: "2024-01-14 15:32:00",
			status: "completed",
		},
		{
			id: "snap_j0k1l2",
			type: "Scheduled",
			size: "23.8 MB",
			createdAt: "2024-01-14 10:00:00",
			status: "completed",
		},
	];

	return (
		<Tabs defaultValue="insights">
			<TabsList>
				<TabsTrigger value="insights">Insights</TabsTrigger>
				<TabsTrigger value="query">SQL Editor</TabsTrigger>
				<TabsTrigger value="tables">Tables</TabsTrigger>
				<TabsTrigger value="backups">Backups</TabsTrigger>
			</TabsList>

			<TabsContent value="insights" className="mt-4 space-y-4">
				<div className="grid gap-4 md:grid-cols-4">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Total Queries
							</CardTitle>
							<BarChart3 className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">31,962</div>
							<p className="text-xs text-muted-foreground">Last 24 hours</p>
						</CardContent>
					</Card>
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Avg Exec Time
							</CardTitle>
							<Clock className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">3.2ms</div>
							<p className="text-xs text-muted-foreground">P95: 12ms</p>
						</CardContent>
					</Card>
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Rows Read</CardTitle>
							<Database className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">293K</div>
							<p className="text-xs text-muted-foreground">Last 24 hours</p>
						</CardContent>
					</Card>
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Rows Written
							</CardTitle>
							<Database className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">7,621</div>
							<p className="text-xs text-muted-foreground">Last 24 hours</p>
						</CardContent>
					</Card>
				</div>

				<Card>
					<CardHeader>
						<CardTitle>Top Queries by Execution Time</CardTitle>
						<CardDescription>
							Most expensive queries in the last 24 hours
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Query</TableHead>
									<TableHead>Calls</TableHead>
									<TableHead>Avg Time</TableHead>
									<TableHead>Rows Read</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{topQueries.map((q, i) => (
									<TableRow key={i}>
										<TableCell className="font-mono text-xs max-w-[300px] truncate">
											{q.query}
										</TableCell>
										<TableCell>{q.calls.toLocaleString()}</TableCell>
										<TableCell>{q.avgTime}</TableCell>
										<TableCell>{q.rowsRead.toLocaleString()}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</TabsContent>

			<TabsContent value="query" className="mt-4 space-y-4">
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle>SQL Query</CardTitle>
								<CardDescription>
									Execute SQL queries against your database
								</CardDescription>
							</div>
							<Button variant="outline" size="sm" disabled>
								<Sparkles className="mr-2 h-4 w-4" />
								Generate with AI
							</Button>
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						<Textarea
							value={query}
							onChange={(e) => {
								setQuery(e.target.value);
							}}
							placeholder="SELECT * FROM users LIMIT 10;"
							className="min-h-[120px] font-mono text-sm"
						/>
						<div className="flex items-center justify-between">
							<span className="text-xs text-muted-foreground">
								Press Cmd/Ctrl + Enter to execute
							</span>
							<Button>
								<Play className="mr-2 h-4 w-4" />
								Execute
							</Button>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Results</CardTitle>
						<CardDescription>5 rows returned in 12ms</CardDescription>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>id</TableHead>
									<TableHead>name</TableHead>
									<TableHead>email</TableHead>
									<TableHead>active</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{userData.map((user) => (
									<TableRow key={user.id}>
										<TableCell className="font-mono">{user.id}</TableCell>
										<TableCell>{user.name}</TableCell>
										<TableCell className="font-mono text-sm">
											{user.email}
										</TableCell>
										<TableCell>
											<Tag variant={user.active ? "success" : "default"}>
												{user.active ? "Yes" : "No"}
											</Tag>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</TabsContent>

			<TabsContent value="tables" className="mt-4">
				<div className="grid grid-cols-1 gap-4 md:grid-cols-4">
					<Card className="md:col-span-1">
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-base">Tables</CardTitle>
							<Button variant="ghost" size="icon" className="h-8 w-8">
								<RefreshCw className="h-4 w-4" />
							</Button>
						</CardHeader>
						<CardContent>
							<div className="space-y-1">
								{tables.map((table) => (
									<button
										key={table.name}
										onClick={() => {
											setSelectedTable(table.name);
										}}
										className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
											selectedTable === table.name ? "bg-muted font-medium" : ""
										}`}
									>
										<div className="flex items-center gap-2">
											<TableIcon className="h-4 w-4 shrink-0" />
											<span>{table.name}</span>
										</div>
										<span className="text-xs text-muted-foreground">
											{table.rows}
										</span>
									</button>
								))}
							</div>
						</CardContent>
					</Card>

					<Card className="md:col-span-3">
						<CardHeader>
							<CardTitle>{selectedTable}</CardTitle>
							<CardDescription>
								{tables.find((t) => t.name === selectedTable)?.rows} total rows
							</CardDescription>
						</CardHeader>
						<CardContent>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>id</TableHead>
										<TableHead>name</TableHead>
										<TableHead>email</TableHead>
										<TableHead>active</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{userData.map((user) => (
										<TableRow key={user.id}>
											<TableCell className="font-mono">{user.id}</TableCell>
											<TableCell>{user.name}</TableCell>
											<TableCell className="font-mono text-sm">
												{user.email}
											</TableCell>
											<TableCell>
												<Tag variant={user.active ? "success" : "default"}>
													{user.active ? "Yes" : "No"}
												</Tag>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				</div>
			</TabsContent>

			<TabsContent value="backups" className="mt-4 space-y-4">
				<div className="grid gap-4 md:grid-cols-3">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Latest Backup
							</CardTitle>
							<CheckCircle2 className="h-4 w-4 text-green-500" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">4 hours ago</div>
							<p className="text-xs text-muted-foreground">
								Next backup in 2 hours
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Total Size</CardTitle>
							<Database className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">96.7 MB</div>
							<p className="text-xs text-muted-foreground">4 snapshots</p>
						</CardContent>
					</Card>
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Retention Period
							</CardTitle>
							<History className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">7 days</div>
							<p className="text-xs text-muted-foreground">
								Point-in-time recovery
							</p>
						</CardContent>
					</Card>
				</div>

				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle>Snapshots</CardTitle>
								<CardDescription>
									Database backups for point-in-time recovery
								</CardDescription>
							</div>
							<Button variant="outline" size="sm" disabled>
								<History className="mr-2 h-4 w-4" />
								Create Snapshot
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>ID</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Size</TableHead>
									<TableHead>Created</TableHead>
									<TableHead>Status</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{backups.map((backup) => (
									<TableRow key={backup.id}>
										<TableCell className="font-mono text-xs">
											{backup.id}
										</TableCell>
										<TableCell>
											<Tag>{backup.type}</Tag>
										</TableCell>
										<TableCell>{backup.size}</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{backup.createdAt}
										</TableCell>
										<TableCell>
											<Tag
												variant="success"
												icon={<CheckCircle2 className="h-3 w-3" />}
											>
												Completed
											</Tag>
										</TableCell>
										<TableCell>
											<Button variant="ghost" size="sm" disabled>
												Restore
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</TabsContent>
		</Tabs>
	);
}

export function QueuesDemo() {
	const metrics = {
		pending: 42,
		processing: 3,
		acknowledged: 15847,
		failed: 12,
		deadLetter: 2,
	};

	const messages = [
		{
			id: "msg_a1b2c3d4",
			status: "ACKNOWLEDGED",
			payload: { type: "email", to: "user@example.com" },
			attempt: 1,
			createdAt: "2024-01-15T10:30:00Z",
		},
		{
			id: "msg_e5f6g7h8",
			status: "PROCESSING",
			payload: { type: "webhook", url: "https://api.example.com" },
			attempt: 1,
			createdAt: "2024-01-15T10:29:55Z",
		},
		{
			id: "msg_i9j0k1l2",
			status: "PENDING",
			payload: { type: "image", action: "resize" },
			attempt: 0,
			createdAt: "2024-01-15T10:29:50Z",
		},
		{
			id: "msg_m3n4o5p6",
			status: "FAILED",
			payload: { type: "notification", userId: 123 },
			attempt: 3,
			createdAt: "2024-01-15T10:25:00Z",
		},
		{
			id: "msg_q7r8s9t0",
			status: "DEAD_LETTER",
			payload: { type: "sync", source: "external" },
			attempt: 5,
			createdAt: "2024-01-15T09:00:00Z",
		},
	];

	return (
		<div className="space-y-6">
			<div className="grid gap-4 md:grid-cols-5">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Pending</CardTitle>
						<Clock className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{metrics.pending}</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Processing</CardTitle>
						<Loader2 className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{metrics.processing}</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Acknowledged</CardTitle>
						<CheckCircle2 className="h-4 w-4 text-green-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{metrics.acknowledged.toLocaleString()}
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Failed</CardTitle>
						<XCircle className="h-4 w-4 text-red-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{metrics.failed}</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Dead Letter</CardTitle>
						<MailWarning className="h-4 w-4 text-orange-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{metrics.deadLetter}</div>
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Recent Messages</CardTitle>
					<CardDescription>
						Latest messages in this queue (auto-refreshes every 5 seconds)
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>ID</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Payload</TableHead>
								<TableHead>Attempt</TableHead>
								<TableHead>Created</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{messages.map((message) => (
								<TableRow key={message.id}>
									<TableCell className="font-mono text-xs">
										{message.id.slice(0, 12)}...
									</TableCell>
									<TableCell>
										<QueueStatusBadge status={message.status} />
									</TableCell>
									<TableCell className="max-w-[200px] truncate font-mono text-xs">
										{JSON.stringify(message.payload)}
									</TableCell>
									<TableCell>{message.attempt}</TableCell>
									<TableCell className="text-xs text-muted-foreground">
										<Time timestamp={message.createdAt} />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</div>
	);
}

function QueueStatusBadge({ status }: { status: string }) {
	switch (status) {
		case "PENDING":
			return <Tag icon={<Clock className="h-3 w-3" />}>Pending</Tag>;
		case "PROCESSING":
			return (
				<Tag variant="blue" icon={<Loader2 className="h-3 w-3 animate-spin" />}>
					Processing
				</Tag>
			);
		case "ACKNOWLEDGED":
			return (
				<Tag variant="success" icon={<CheckCircle2 className="h-3 w-3" />}>
					Acknowledged
				</Tag>
			);
		case "FAILED":
			return (
				<Tag variant="error" icon={<XCircle className="h-3 w-3" />}>
					Failed
				</Tag>
			);
		case "DEAD_LETTER":
			return (
				<Tag variant="orange" icon={<AlertCircle className="h-3 w-3" />}>
					Dead Letter
				</Tag>
			);
		default:
			return <Tag>{status}</Tag>;
	}
}

export function WorkflowsDemo() {
	const executions = [
		{
			id: "exec_a1b2c3",
			status: "COMPLETED",
			stepsCompleted: 4,
			stepsCount: 4,
			durationMs: 2340,
			startedAt: "2024-01-15T10:30:00Z",
		},
		{
			id: "exec_d4e5f6",
			status: "RUNNING",
			stepsCompleted: 2,
			stepsCount: 4,
			durationMs: null,
			startedAt: "2024-01-15T10:29:45Z",
		},
		{
			id: "exec_g7h8i9",
			status: "COMPLETED",
			stepsCompleted: 4,
			stepsCount: 4,
			durationMs: 1890,
			startedAt: "2024-01-15T10:25:00Z",
		},
		{
			id: "exec_j0k1l2",
			status: "FAILED",
			stepsCompleted: 3,
			stepsCount: 4,
			durationMs: 5420,
			startedAt: "2024-01-15T10:20:00Z",
		},
		{
			id: "exec_m3n4o5",
			status: "CANCELLED",
			stepsCompleted: 1,
			stepsCount: 4,
			durationMs: 450,
			startedAt: "2024-01-15T10:15:00Z",
		},
	];

	return (
		<Card>
			<CardHeader>
				<CardTitle>Recent Executions</CardTitle>
				<CardDescription>
					Workflow executions (auto-refreshes every 5 seconds)
				</CardDescription>
			</CardHeader>
			<CardContent>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>ID</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>Steps</TableHead>
							<TableHead>Duration</TableHead>
							<TableHead>Started</TableHead>
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						{executions.map((execution) => (
							<TableRow key={execution.id}>
								<TableCell className="font-mono text-xs">
									{execution.id.slice(0, 12)}...
								</TableCell>
								<TableCell>
									<ExecutionStatusBadge status={execution.status} />
								</TableCell>
								<TableCell>
									<span className="text-sm">
										{execution.stepsCompleted}/{execution.stepsCount}
									</span>
								</TableCell>
								<TableCell className="text-sm">
									{formatDuration(execution.durationMs)}
								</TableCell>
								<TableCell className="text-xs text-muted-foreground">
									<Time timestamp={execution.startedAt} />
								</TableCell>
								<TableCell>
									<span className="flex items-center text-sm text-primary cursor-pointer hover:underline">
										View
										<ChevronRight className="h-4 w-4" />
									</span>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	);
}

function ExecutionStatusBadge({ status }: { status: string }) {
	switch (status) {
		case "PENDING":
			return <Tag icon={<Clock className="h-3 w-3" />}>Pending</Tag>;
		case "RUNNING":
			return (
				<Tag variant="blue" icon={<Loader2 className="h-3 w-3 animate-spin" />}>
					Running
				</Tag>
			);
		case "COMPLETED":
			return (
				<Tag variant="success" icon={<CheckCircle2 className="h-3 w-3" />}>
					Completed
				</Tag>
			);
		case "FAILED":
			return (
				<Tag variant="error" icon={<XCircle className="h-3 w-3" />}>
					Failed
				</Tag>
			);
		case "CANCELLED":
			return <Tag icon={<Ban className="h-3 w-3" />}>Cancelled</Tag>;
		default:
			return <Tag>{status}</Tag>;
	}
}

export function AIDemo() {
	const [prompt, setPrompt] = useState("");

	const exampleQueries = [
		{
			prompt: "Show me users who signed up this month",
			sql: "SELECT * FROM users WHERE created_at >= DATE_TRUNC('month', CURRENT_DATE)",
		},
		{
			prompt: "Top 10 products by revenue",
			sql: "SELECT p.name, SUM(oi.quantity * oi.price) as revenue\nFROM products p\nJOIN order_items oi ON p.id = oi.product_id\nGROUP BY p.id, p.name\nORDER BY revenue DESC\nLIMIT 10",
		},
		{
			prompt: "Users with more than 5 orders",
			sql: "SELECT u.name, u.email, COUNT(o.id) as order_count\nFROM users u\nJOIN orders o ON u.id = o.user_id\nGROUP BY u.id, u.name, u.email\nHAVING COUNT(o.id) > 5",
		},
	];

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Sparkles className="h-5 w-5 text-cyan-500" />
						AI Query Generator
					</CardTitle>
					<CardDescription>
						Describe what you want in natural language and AI will generate the
						SQL query
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<Textarea
						value={prompt}
						onChange={(e) => {
							setPrompt(e.target.value);
						}}
						placeholder="e.g., Show me the top 10 users by order count, including their email and total orders"
						className="min-h-[100px]"
					/>
					<div className="flex justify-end">
						<Button>
							<Sparkles className="mr-2 h-4 w-4" />
							Generate Query
						</Button>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Example Queries</CardTitle>
					<CardDescription>
						See how natural language translates to SQL
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-6">
						{exampleQueries.map((example, index) => (
							<div key={index} className="space-y-2">
								<div className="flex items-center gap-2">
									<Tag>Prompt</Tag>
									<span className="text-sm">{example.prompt}</span>
								</div>
								<div className="rounded-lg border border-border bg-zinc-950 p-4">
									<pre className="text-sm text-zinc-300 font-mono whitespace-pre-wrap">
										{example.sql}
									</pre>
								</div>
							</div>
						))}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

export function AuthDemo() {
	const users = [
		{
			id: "usr_a1b2c3d4",
			email: "alice@example.com",
			emailVerified: true,
			createdAt: "2024-01-15T10:30:00Z",
			lastSignIn: "2 minutes ago",
		},
		{
			id: "usr_e5f6g7h8",
			email: "bob@example.com",
			emailVerified: true,
			createdAt: "2024-01-14T08:15:00Z",
			lastSignIn: "1 hour ago",
		},
		{
			id: "usr_i9j0k1l2",
			email: "carol@example.com",
			emailVerified: false,
			createdAt: "2024-01-13T14:45:00Z",
			lastSignIn: "3 days ago",
		},
		{
			id: "usr_m3n4o5p6",
			email: "david@example.com",
			emailVerified: true,
			createdAt: "2024-01-12T09:00:00Z",
			lastSignIn: "5 hours ago",
		},
		{
			id: "usr_q7r8s9t0",
			email: "eve@example.com",
			emailVerified: true,
			createdAt: "2024-01-10T16:20:00Z",
			lastSignIn: "1 day ago",
		},
	];

	const sessions = [
		{
			id: "sess_abc123",
			userId: "usr_a1b2c3d4",
			email: "alice@example.com",
			expiresAt: "2024-01-22T10:30:00Z",
			createdAt: "2024-01-15T10:30:00Z",
			revoked: false,
		},
		{
			id: "sess_def456",
			userId: "usr_e5f6g7h8",
			email: "bob@example.com",
			expiresAt: "2024-01-21T08:15:00Z",
			createdAt: "2024-01-14T08:15:00Z",
			revoked: false,
		},
		{
			id: "sess_ghi789",
			userId: "usr_a1b2c3d4",
			email: "alice@example.com",
			expiresAt: "2024-01-20T10:30:00Z",
			createdAt: "2024-01-13T10:30:00Z",
			revoked: true,
		},
	];

	return (
		<div className="space-y-6">
			<div className="grid gap-4 md:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total Users</CardTitle>
						<Users className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">1,247</div>
						<p className="text-xs text-muted-foreground">+12 this week</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Active Sessions
						</CardTitle>
						<Key className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">342</div>
						<p className="text-xs text-muted-foreground">Currently active</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Sign Ups Today
						</CardTitle>
						<UserPlus className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">18</div>
						<p className="text-xs text-muted-foreground">+5 from yesterday</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Email Verified
						</CardTitle>
						<ShieldCheck className="h-4 w-4 text-green-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">94%</div>
						<p className="text-xs text-muted-foreground">1,172 of 1,247</p>
					</CardContent>
				</Card>
			</div>

			<Tabs defaultValue="users">
				<TabsList>
					<TabsTrigger value="users">Users</TabsTrigger>
					<TabsTrigger value="sessions">Sessions</TabsTrigger>
				</TabsList>

				<TabsContent value="users" className="mt-4">
					<Card>
						<CardHeader>
							<CardTitle>Recent Users</CardTitle>
							<CardDescription>Users who signed up recently</CardDescription>
						</CardHeader>
						<CardContent>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>ID</TableHead>
										<TableHead>Email</TableHead>
										<TableHead>Verified</TableHead>
										<TableHead>Created</TableHead>
										<TableHead>Last Sign In</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{users.map((user) => (
										<TableRow key={user.id}>
											<TableCell className="font-mono text-xs">
												{user.id.slice(0, 12)}...
											</TableCell>
											<TableCell className="font-mono text-sm">
												{user.email}
											</TableCell>
											<TableCell>
												{user.emailVerified ? (
													<Tag
														variant="success"
														icon={<CheckCircle2 className="h-3 w-3" />}
													>
														Verified
													</Tag>
												) : (
													<Tag icon={<Clock className="h-3 w-3" />}>
														Pending
													</Tag>
												)}
											</TableCell>
											<TableCell className="text-sm text-muted-foreground">
												{new Date(user.createdAt).toLocaleDateString()}
											</TableCell>
											<TableCell className="text-sm text-muted-foreground">
												{user.lastSignIn}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="sessions" className="mt-4">
					<Card>
						<CardHeader>
							<CardTitle>Active Sessions</CardTitle>
							<CardDescription>User sessions with 7-day expiry</CardDescription>
						</CardHeader>
						<CardContent>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Session ID</TableHead>
										<TableHead>User</TableHead>
										<TableHead>Status</TableHead>
										<TableHead>Created</TableHead>
										<TableHead>Expires</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{sessions.map((session) => (
										<TableRow key={session.id}>
											<TableCell className="font-mono text-xs">
												{session.id}
											</TableCell>
											<TableCell className="font-mono text-sm">
												{session.email}
											</TableCell>
											<TableCell>
												{session.revoked ? (
													<Tag
														variant="error"
														icon={<XCircle className="h-3 w-3" />}
													>
														Revoked
													</Tag>
												) : (
													<Tag
														variant="success"
														icon={<Lock className="h-3 w-3" />}
													>
														Active
													</Tag>
												)}
											</TableCell>
											<TableCell className="text-sm text-muted-foreground">
												{new Date(session.createdAt).toLocaleDateString()}
											</TableCell>
											<TableCell className="text-sm text-muted-foreground">
												{new Date(session.expiresAt).toLocaleDateString()}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>
		</div>
	);
}

function CodeBlock({ filename, code }: { filename: string; code: string }) {
	return (
		<div className="rounded-lg border border-border bg-zinc-950 overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-800">
				<div className="flex gap-1.5">
					<div className="w-3 h-3 rounded-full bg-red-500" />
					<div className="w-3 h-3 rounded-full bg-yellow-500" />
					<div className="w-3 h-3 rounded-full bg-green-500" />
				</div>
				<span className="text-sm text-zinc-400">{filename}</span>
			</div>
			<pre className="p-4 overflow-x-auto">
				<code className="text-sm text-zinc-300 font-mono whitespace-pre">
					{code}
				</code>
			</pre>
		</div>
	);
}

export function CronDemo() {
	const runs = [
		{
			trigger: "EVERY_10_SECONDS",
			expression: "*/10 * * * * *",
			status: "success",
			duration: 42,
			when: "8 seconds ago",
		},
		{
			trigger: "HOURLY_CLEANUP",
			expression: "0 * * * *",
			status: "success",
			duration: 1860,
			when: "14 minutes ago",
		},
		{
			trigger: "DAILY_REPORT",
			expression: "0 9 * * *",
			status: "running",
			duration: 0,
			when: "just now",
		},
		{
			trigger: "WEEKLY_DIGEST",
			expression: "0 10 * * 1",
			status: "success",
			duration: 5210,
			when: "2 days ago",
		},
	];

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Cron Runs</CardTitle>
					<CardDescription>
						Every trigger in ploy.yaml, with its last run
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Trigger</TableHead>
								<TableHead>Expression</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Duration</TableHead>
								<TableHead>Last run</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{runs.map((run) => (
								<TableRow key={run.trigger}>
									<TableCell className="font-medium">{run.trigger}</TableCell>
									<TableCell>
										<code className="text-xs font-mono text-muted-foreground">
											{run.expression}
										</code>
									</TableCell>
									<TableCell>
										{run.status === "running" ? (
											<Tag
												variant="blue"
												icon={<Loader2 className="h-3 w-3 animate-spin" />}
											>
												Running
											</Tag>
										) : (
											<Tag
												variant="success"
												icon={<CheckCircle2 className="h-3 w-3" />}
											>
												Success
											</Tag>
										)}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{run.duration ? formatDuration(run.duration) : "—"}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{run.when}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Scheduled Handler</CardTitle>
					<CardDescription>
						One handler receives every trigger; branch on event.cron
					</CardDescription>
				</CardHeader>
				<CardContent>
					<CodeBlock
						filename="src/index.ts"
						code={`export default {
  async scheduled(event, env) {
    if (event.cron === "0 9 * * *") {
      await env.TASKS.send({ job: "daily-report" });
    }
  },
} satisfies Ploy;`}
					/>
				</CardContent>
			</Card>
		</div>
	);
}

export function StorageDemo() {
	const entries = [
		{ key: "stats", value: '{ "visits": 10482, "signups": 231 }', ttl: null },
		{ key: "user:123", value: '{ "role": "admin", "visits": 42 }', ttl: null },
		{ key: "feature:dark-mode", value: '"true"', ttl: null },
		{ key: "report:q3", value: "<cached 14 KB>", ttl: "9m 12s" },
		{ key: "ratelimit:203.0.113.4", value: '"1"', ttl: "0.4s" },
	];

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Keys</CardTitle>
					<CardDescription>
						Durable state has no expiry; cache entries carry a TTL
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Key</TableHead>
								<TableHead>Value</TableHead>
								<TableHead>Store</TableHead>
								<TableHead>TTL</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{entries.map((entry) => (
								<TableRow key={entry.key}>
									<TableCell className="font-mono text-sm">
										{entry.key}
									</TableCell>
									<TableCell className="font-mono text-sm text-muted-foreground">
										{entry.value}
									</TableCell>
									<TableCell>
										{entry.ttl ? (
											<Tag variant="blue">CACHE</Tag>
										) : (
											<Tag>STATE</Tag>
										)}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{entry.ttl ?? "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Atomic Update</CardTitle>
					<CardDescription>
						Increment a counter without reading it first
					</CardDescription>
				</CardHeader>
				<CardContent>
					<CodeBlock
						filename="src/index.ts"
						code={`await env.STATE.update("user:123", {
  $inc: { visits: 1 },
  $set: { lastSeen: Date.now() },
});`}
					/>
				</CardContent>
			</Card>
		</div>
	);
}

export function FileStorageDemo() {
	const files = [
		{ key: "uploads/user-123/avatar.png", type: "image/png", size: "48 KB" },
		{
			key: "documents/invoices/inv-001.pdf",
			type: "application/pdf",
			size: "212 KB",
		},
		{ key: "exports/2026-10-01.csv", type: "text/csv", size: "1.4 MB" },
		{ key: "config/app.json", type: "application/json", size: "312 B" },
	];

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Files</CardTitle>
					<CardDescription>
						Everything your worker has stored, with its content type
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Key</TableHead>
								<TableHead>Content type</TableHead>
								<TableHead>Size</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{files.map((file) => (
								<TableRow key={file.key}>
									<TableCell className="font-mono text-sm">
										{file.key}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{file.type}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{file.size}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>List by Prefix</CardTitle>
					<CardDescription>
						Group files under prefixes and list a folder in one call
					</CardDescription>
				</CardHeader>
				<CardContent>
					<CodeBlock
						filename="src/index.ts"
						code={`const { keys } = await env.FILES.list({
  prefix: "uploads/user-123/",
});
// ["uploads/user-123/avatar.png"]`}
					/>
				</CardContent>
			</Card>
		</div>
	);
}

export function SandboxesDemo() {
	const lines = [
		{ kind: "cmd", text: "$ sandbox.gitCheckout(https://github.com/acme/app)" },
		{ kind: "out", text: "Cloning into 'app'... done (depth 1)" },
		{ kind: "cmd", text: "$ sandbox.exec(pnpm install, { cwd: app })" },
		{ kind: "out", text: "Packages: +412 · Done in 6.2s" },
		{ kind: "cmd", text: "$ sandbox.startProcess(pnpm test, { cwd: app })" },
		{ kind: "out", text: "{ id: proc_8f21, status: running, pid: 418 }" },
		{ kind: "cmd", text: "$ sandbox.getProcess(proc_8f21)" },
		{ kind: "ok", text: "{ status: completed, exitCode: 0 }" },
	];

	return (
		<div className="space-y-6">
			<div className="grid gap-4 md:grid-cols-3">
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium">Sandbox</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold font-mono">session-42</div>
						<p className="text-xs text-muted-foreground">
							Persists across requests
						</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium">Processes</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">1 running</div>
						<p className="text-xs text-muted-foreground">3 completed</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium">AI</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">Enabled</div>
						<p className="text-xs text-muted-foreground">
							Gateway credentials injected
						</p>
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Session Log</CardTitle>
					<CardDescription>
						Commands run from a worker against one long-lived sandbox
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="rounded-lg border border-border bg-zinc-950 p-4 font-mono text-sm space-y-1">
						{lines.map((line) => (
							<div
								key={line.text}
								className={
									line.kind === "cmd"
										? "text-zinc-100"
										: line.kind === "ok"
											? "text-green-400"
											: "text-zinc-500"
								}
							>
								{line.text}
							</div>
						))}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

export function PloyGitDemo() {
	const pushes = [
		{
			ref: "main",
			commit: "feat: add billing webhook",
			source: "laptop",
			status: "success",
			url: "https://myapp.ploy.app",
		},
		{
			ref: "agent/fix-tests",
			commit: "fix: flaky date test",
			source: "ci",
			status: "building",
			url: "https://agent-fix-tests-myapp.ploy.app",
		},
		{
			ref: "main",
			commit: "chore: bump deps",
			source: "laptop",
			status: "success",
			url: "https://myapp.ploy.app",
		},
	];

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Add the Remote</CardTitle>
					<CardDescription>
						A push URL is a normal git remote with a short-lived credential
					</CardDescription>
				</CardHeader>
				<CardContent>
					<CodeBlock
						filename="terminal"
						code={`$ ploy git remote --project proj_123 --ttl 30d --add
Added remote "ploy" (expires in 30 days)

$ git push -u ploy main
remote: Deploying 4f2a9c1 to production...
remote: https://myapp.ploy.app`}
					/>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Recent Pushes</CardTitle>
					<CardDescription>
						Production from main, previews from every other branch
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Branch</TableHead>
								<TableHead>Commit</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>URL</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{pushes.map((push) => (
								<TableRow key={`${push.ref}-${push.commit}`}>
									<TableCell>
										<div className="flex items-center gap-2">
											<GitBranch className="h-4 w-4 text-muted-foreground" />
											<span className="font-medium">{push.ref}</span>
										</div>
									</TableCell>
									<TableCell className="text-sm">{push.commit}</TableCell>
									<TableCell>
										<DeploymentStatusBadge status={push.status} />
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{push.url}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</div>
	);
}

export function SelfHostingDemo() {
	const services = [
		{ name: "ui", role: "Dashboard", port: 3002 },
		{ name: "api", role: "Control plane", port: 4002 },
		{ name: "handler", role: "Serves deployments", port: 4001 },
		{ name: "builder", role: "Builds pushes", port: null },
		{ name: "worker", role: "Background jobs", port: null },
		{ name: "postgres", role: "Platform database", port: 5432 },
	];

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Start the Stack</CardTitle>
					<CardDescription>
						One compose file brings up every service
					</CardDescription>
				</CardHeader>
				<CardContent>
					<CodeBlock
						filename="terminal"
						code={`$ git clone https://github.com/meetploy/ploy.git && cd ploy
$ cp .env.example .env
$ docker compose up -d
 ✔ Container ploy-postgres   Healthy
 ✔ Container ploy-api        Started
 ✔ Container ploy-builder    Started
 ✔ Container ploy-handler    Started
 ✔ Container ploy-ui         Started`}
					/>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Services</CardTitle>
					<CardDescription>
						The same components that run the managed cloud
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Service</TableHead>
								<TableHead>Role</TableHead>
								<TableHead>Port</TableHead>
								<TableHead>Status</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{services.map((service) => (
								<TableRow key={service.name}>
									<TableCell className="font-mono text-sm">
										{service.name}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{service.role}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{service.port ?? "—"}
									</TableCell>
									<TableCell>
										<Tag
											variant="success"
											icon={<CheckCircle2 className="h-3 w-3" />}
										>
											Healthy
										</Tag>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</div>
	);
}
